/** Rank ladder and medal criteria (02 §11). Criteria are data; src/meta/aftermath/medals.ts reads them. */

import type { ArchetypeId } from '../../sim/types';
import type { MedalId } from '../types';

/** Cumulative merit needed for each of the twelve ranks, PFC to COL. */
export const RANK_THRESHOLDS: readonly number[] = [
  0, 1500, 4000, 8000, 13000, 20000, 28000, 38000, 50000, 65000, 85000, 110000,
];

export const RANK_NAMES: readonly string[] = [
  'Private First Class', 'Corporal', 'Sergeant', 'Staff Sergeant', 'Platoon Sergeant', 'Sergeant Major',
  'Second Lieutenant', 'First Lieutenant', 'Captain', 'Major', 'Lieutenant Colonel', 'Colonel',
];

/** COL needs a won Operation (or a Classic Campaign) as well as the merit. */
export const CAMPAIGN_ONLY_RANK = 11;

export interface MedalInfo {
  id: MedalId;
  name: string;
  text: string;
  /** Earning it marks the ranger Decorated (04 §4.3). */
  topTier: boolean;
}

export const MEDALS: readonly MedalInfo[] = [
  { id: 'commendation-1', name: 'Army Commendation', topTier: false, text: 'A success scoring 2 000 or more.' },
  { id: 'commendation-2', name: 'Second Army Commendation', topTier: false,
    text: 'A second 2 000-point success, at threat rating 3 or higher.' },
  { id: 'bronze-star', name: 'Bronze Star', topTier: false, text: 'A success at threat 4+ scoring 3 500 or more.' },
  // R6: `silverStarDeed` has four limbs and this listed three - the span dropped under the convoy
  // (mission 13) was missing, and the Medal of Honor, which is "a Silver Star deed", inherited it.
  { id: 'silver-star', name: 'Silver Star', topTier: true,
    text: 'A success at threat 6+ as a ghost, with the bridge dropped under the convoy, with every escort alive, '
      + 'or with every target destroyed.' },
  { id: 'dsc', name: 'Distinguished Service Cross', topTier: true,
    text: 'A success at threat 7+ while carrying two wounds, or a rescue that frees two or more captured rangers.' },
  { id: 'medal-of-honor', name: 'Medal of Honor', topTier: true,
    text: 'Operations only: a Silver Star deed at threat 8+. May be posthumous.' },
  { id: 'purple-heart', name: 'Purple Heart', topTier: false, text: 'Wounded in action, or killed.' },
  { id: 'pow-medal', name: 'P.O.W. Medal', topTier: false, text: 'Captured, and brought home.' },
  { id: 'evaders-ribbon', name: "Evader's Ribbon", topTier: false, text: 'Missed the pickup and walked out.' },
  { id: 'pathfinder-badge', name: 'Pathfinder Badge', topTier: false,
    text: 'Five landings within 5 m of the map marker.' },
];

export const MEDAL_RULES = {
  commendationScore: 2000,
  secondCommendationThreat: 3,
  bronzeThreat: 4,
  bronzeScore: 3500,
  silverThreat: 6,
  /** Ghost bonus: the garrison alert never rose above this (02 §11). */
  ghostMaxAlert: 1,
  dscThreat: 7,
  dscWounds: 2,
  dscFreed: 2,
  honorThreat: 8,
  pathfinderLandings: 5,
  /**
   * Metres from the map marker that still count as a landing on it (`app/summary.ts`). R6: the
   * badge's text said 5 and the code used 12 - 2.4 times the distance and nearly six times the
   * area. The text wins: 12 m is a decent landing, not a precise one, and the number lives HERE,
   * beside the words that promise it, so the two cannot drift apart again.
   */
  preciseLandingMetres: 5,
} as const;

/**
 * Most captives an escort mission can hold (03 §2). The summary carries no total, so "all
 * escorts alive" is only provable when the count saved reaches this (or the optional
 * `captivesTotal` extra says so).
 */
export const ESCORT_MAX: Partial<Record<ArchetypeId, number>> = {
  'capture-officer': 1,
  'pow-camp': 6,
  hostages: 4,
};
